import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import RevealOnScroll from "@/components/RevealOnScroll";

// Asymmetric 3x3-style grid inspired by boutique travel curators:
// - Left column: a single tall "Destination Wedding" hero tile (spans 2 rows)
// - Right column: two smaller tiles stacked (Honeymoon + Candle Light Dinner)
// - Row 2: Ayurveda + India Cruise (below the right column tiles)
// - Row 3: Solo + Luxury Train + India Biking (full-width 3 tiles)
const COLLECTIONS = [
  {
    slug: "wedding",
    title: "Destination Wedding",
    subtitle: "Nothing can be more precious than feeling special",
    img: "https://images.pexels.com/photos/33417236/pexels-photo-33417236.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
    span: "row-span-2", // tall left tile
  },
  {
    slug: "honeymoon",
    title: "Honeymoon Packages",
    subtitle: "Lovers' Paradise · Taj, Kashmir, Kerala & more",
    img: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&q=85&w=1200&h=600",
  },
  {
    slug: "ayurveda",
    title: "Ayurveda Packages",
    subtitle: "Ayurveda & Yoga Tour Packages",
    img: "https://images.pexels.com/photos/6187418/pexels-photo-6187418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200",
  },
  {
    slug: "cruise",
    title: "India Cruise Tours",
    subtitle: "Cruise Tours & Packages",
    img: "https://images.pexels.com/photos/32285409/pexels-photo-32285409.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200",
  },
  {
    slug: "solo",
    title: "Solo Tours",
    subtitle: "Organised trips for solo travellers",
    img: "https://images.pexels.com/photos/6081397/pexels-photo-6081397.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200",
  },
  {
    slug: "luxury-train",
    title: "Luxury Train Tours",
    subtitle: "Visit India by the most luxurious trains in India",
    img: "https://images.pexels.com/photos/38204413/pexels-photo-38204413.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200",
  },
  {
    slug: "biking",
    title: "India Biking Tours",
    subtitle: "Adventure motorcycle tours",
    img: "https://images.pexels.com/photos/1070386/pexels-photo-1070386.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200",
  },
];

const CollectionTile = ({ c, tall = false }) => (
  <Link
    to={`/themes/${c.slug}`}
    data-testid={`collection-${c.slug}`}
    className={`group relative overflow-hidden rounded-sm block ${tall ? "h-[560px] md:h-full min-h-[560px]" : "aspect-[16/9]"}`}
  >
    <img
      src={c.img}
      alt={c.title}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />
    <div className="absolute inset-0 z-10 flex flex-col justify-end p-7 md:p-8 text-white pointer-events-none">
      <h3 className={`font-serif tracking-tight leading-tight ${tall ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"}`}>
        {c.title}
      </h3>
      {c.subtitle && (
        <p className="italic text-white/85 text-sm md:text-base mt-2 max-w-md">{c.subtitle}</p>
      )}
      <div className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-marigold group-hover:text-white transition-colors">
        Discover <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </div>
  </Link>
);

export default function SpecialCollections() {
  const [wedding, honeymoon, ayurveda, cruise, solo, luxTrain, biking] = COLLECTIONS;
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="special-collections">
      <div className="container-tti">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <RevealOnScroll>
            <div className="eyebrow mb-4">Special · Unique Collections</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Curated for <span className="italic text-deepred">every kind of love</span>
            </h2>
            <p className="mt-5 text-foreground/70 text-lg font-light leading-relaxed">
              Weddings, honeymoons, wellness, cruises, luxury trains and biking — signature journeys hand-crafted for special moments.
            </p>
          </RevealOnScroll>
        </div>

        {/* Row 1: 12-col split — 5 (tall wedding) + 7 (honeymoon + ayurveda stacked) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-5">
          <div className="lg:col-span-5">
            <CollectionTile c={wedding} tall />
          </div>
          <div className="lg:col-span-7 grid gap-5">
            <CollectionTile c={honeymoon} />
            <CollectionTile c={ayurveda} />
          </div>
        </div>

        {/* Row 2: 4 tiles - Cruise + Solo + Luxury Train + Biking */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <CollectionTile c={cruise} />
          <CollectionTile c={solo} />
          <CollectionTile c={luxTrain} />
          <CollectionTile c={biking} />
        </div>
      </div>
    </section>
  );
}
